import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { atlasClient } from '../../../api/atlasClient';
import { useAtlasStore, AtlasState } from '../../../store/atlasStore';
import { getJobDisplayStatus } from '../hooks/useAnimatedProgress';

/**
 * Global Spark polling provider.
 *
 * One-shot refactor direction: centralizes polling for Spark across all /spark routes.
 * Notes:
 * - Task 1: Implement provider using existing store API without adding new fields yet.
 * - Task 2 will introduce lastActiveSparkProjectFqn and background flags to remove limitations.
 */
export const SparkPollingProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  // Access store pieces via selectors
  const fetchSparkProjectDetails = useAtlasStore((s: AtlasState) => s.fetchSparkProjectDetails);
  const fetchSparkJobs = useAtlasStore((s: AtlasState) => s.fetchSparkJobs);
  const fetchMySparkJobs = useAtlasStore((s: AtlasState) => (s as any).fetchMySparkJobs);
  const fetchSparkProjects = useAtlasStore((s: AtlasState) => s.fetchSparkProjects);
  const lastActiveProjectFqn = useAtlasStore((s: AtlasState) => s.lastActiveSparkProjectFqn);

  // Refs to avoid stale closures and overlapping refreshes
  const lastCompletedCountRef = useRef<number | null>(null);
  const isRefreshingRef = useRef<boolean>(false);
  const lastInboxRefreshAtRef = useRef<number>(0);
  const lastProjectFqnRef = useRef<string | null>(null);
  const lastProjectRefreshAtRef = useRef<number>(0);

  useEffect(() => {
    // Only run polling when under /spark routes. App mounts the provider accordingly, but stay defensive.
    if (!location.pathname.startsWith('/spark')) return;

        const POLL_MS = 3000; // main tick interval
        const INBOX_REFRESH_MS = 15000; // background project list refresh cadence

    let timer: number | null = null;
    let isActive = true; // effect instance liveness flag

    const tick = async () => {
      try {
        // Pause when tab not visible
        if (typeof document !== 'undefined' && (document as any).hidden) {
          return;
        }

        const state = useAtlasStore.getState();
        const isBlocked = state.isUserInteractionBlockingRefresh;

        // Project-scoped polling: only when on a project page
        const onProjectPage = location.pathname.startsWith('/spark/project/');
        const projectFqn = onProjectPage ? (lastActiveProjectFqn || state.currentSparkProject?.fqn || null) : null;
        if (projectFqn) {
          // Reset last-completed counter when switching projects
          if (lastProjectFqnRef.current !== projectFqn) {
            lastProjectFqnRef.current = projectFqn;
            lastCompletedCountRef.current = null;
          }
          // Lightweight check for completed job count + worker health
          try {
            const res = await atlasClient.getSparkProjectCompletedJobCount(projectFqn);
            const newCount = res.completed_job_count;
            // Store worker online and total completed count for UI clarity
            try {
              const st = useAtlasStore.getState();
              if (st.workerIsOnline !== res.worker_is_online) st.setWorkerOnline(res.worker_is_online);
              if (st.currentSparkProjectTotalCompletedCount !== newCount) st.setTotalCompletedForCurrentProject(newCount);
            } catch {}

            const prev = lastCompletedCountRef.current;
            // Initialize ref on first tick for this project
            if (prev === null) {
              lastCompletedCountRef.current = newCount;
            } else if (!isRefreshingRef.current && !isBlocked && newCount > prev) {
              // Full refresh on increase
              isRefreshingRef.current = true;
              // Optimistically record the count to avoid duplicate refreshes if next tick fires early
              lastCompletedCountRef.current = newCount;
              // Simple throttle: avoid refreshing more often than POLL_MS when counts jump rapidly
              const nowTs = Date.now();
              if (nowTs - lastProjectRefreshAtRef.current < POLL_MS - 50) {
                isRefreshingRef.current = false;
              } else {
                await fetchSparkProjectDetails(projectFqn);
                isRefreshingRef.current = false;
                lastProjectRefreshAtRef.current = nowTs;
              }
            } else if (isBlocked && newCount > prev) {
              // Defer heavy project refresh until unblocked, but update job list immediately
              try { useAtlasStore.getState().setPendingProjectRefresh(projectFqn); } catch {}
              try { await fetchSparkJobs(projectFqn); } catch {}
              lastCompletedCountRef.current = newCount; // remember advance to avoid requeueing
            }
          } catch (e) {
            // Swallow network errors; next tick will retry
          }

          // While there are running/pending jobs, refresh the jobs list frequently
          const hasActiveJobs = useAtlasStore.getState().currentSparkJobs?.some((job) => {
            const displayStatus = getJobDisplayStatus(job);
            return displayStatus === 'running' || displayStatus === 'pending';
          });
          if (hasActiveJobs) {
            try {
              await fetchSparkJobs(projectFqn);
            } catch (e) {
              // Non-fatal
            }
          }

        } else {
          // No current project (likely on Inbox/Search). Reset last completed ref.
          lastCompletedCountRef.current = null;
        }

        // Background refresh for Inbox project list
        const now = Date.now();
        const onInbox = location.pathname === '/spark';
        if (onInbox && now - lastInboxRefreshAtRef.current >= INBOX_REFRESH_MS) {
          lastInboxRefreshAtRef.current = now;
          try { await fetchSparkProjects({ background: true }); } catch {/* ignore */}
        }

        // Refresh user's jobs on search page or inbox (for library item jobs)
        if (location.pathname === '/spark' || location.pathname.startsWith('/spark/search')) {
          try { await fetchMySparkJobs(); } catch {}
        }

      } finally {
        // Re-arm interval only if this effect instance is still current
        if (isActive) {
          timer = window.setTimeout(tick, POLL_MS);
        }
      }
    };

    // Prime my-jobs on search page or inbox for library item jobs
    if (location.pathname === '/spark' || location.pathname.startsWith('/spark/search')) {
      (async () => { try { await fetchMySparkJobs(); } catch {} })();
    }

    // Start polling
    if (isActive) {
      timer = window.setTimeout(tick, POLL_MS);
    }

    return () => {
      if (timer) {
        window.clearTimeout(timer);
      }
      // Invalidate this effect instance
      isActive = false;
      isRefreshingRef.current = false;
    };
    // Intentionally depend on route and project; the refs absorb store dynamics
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, lastActiveProjectFqn]);

  return <>{children}</>;
};

export default SparkPollingProvider;
